import type { CustomerSummary } from "@aiyo/shared";
import { ArrowUpRightFromSquare, MapPin, Persons, Xmark } from "@gravity-ui/icons";
import { Button, Card, Chip, Table } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { FilterSelect, SearchBox } from "../components/filters";
import { EmptyBlock, ErrorBlock, LoadingBlock, Page, PageHeader } from "../components/ui";
import { PUBLIC_API_URL } from "../lib/config";
import { useFormat } from "../lib/format";
import { useApps, useCustomers } from "../lib/queries";

export function CustomersPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const apps = useApps();
  const q = params.get("q") ?? undefined;
  const appId = params.get("app") ?? undefined;
  const city = params.get("city") ?? undefined;
  const customers = useCustomers({ q, app_id: appId, city });
  const rows = customers.data?.pages.flatMap((p) => p.items) ?? [];
  const filtered = Boolean(q || appId || city);

  const update = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === "") next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };

  return (
    <Page>
      <PageHeader title={t("customers.title")} description={t("customers.subtitle")} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchBox
          label={t("customers.search")}
          placeholder={t("customers.searchPlaceholder")}
          value={q ?? ""}
          onChange={(v) => update({ q: v })}
          className="w-full sm:flex-1"
        />
        <FilterSelect
          label={t("customers.columns.app")}
          allLabel={t("overview.allApps")}
          value={appId}
          onChange={(v) => update({ app: v })}
          options={(apps.data ?? []).map((a) => ({ id: a.id, label: a.name }))}
          className="w-full sm:w-56"
        />
      </div>

      {city ? (
        <div className="-mt-4 flex">
          <Button
            size="sm"
            variant="secondary"
            aria-label={t("customers.clearCity")}
            onPress={() => update({ city: undefined })}
          >
            <MapPin />
            {t("customers.cityFilter", { city })}
            <Xmark />
          </Button>
        </div>
      ) : null}

      {customers.isPending ? (
        <LoadingBlock />
      ) : customers.isError ? (
        <ErrorBlock error={customers.error} onRetry={() => customers.refetch()} />
      ) : rows.length === 0 && !filtered ? (
        <Card>
          <EmptyBlock
            icon={Persons}
            title={t("customers.empty")}
            hint={t("customers.emptyHint")}
            action={
              <a href={`${PUBLIC_API_URL}/docs`} target="_blank" rel="noreferrer">
                <Button variant="secondary" size="sm">
                  {t("customers.apiReference")}
                  <ArrowUpRightFromSquare />
                </Button>
              </a>
            }
          />
        </Card>
      ) : (
        <div
          className={`flex flex-col gap-4 transition-opacity ${customers.isPlaceholderData ? "opacity-60" : ""}`}
        >
          <CustomerTable rows={rows} onOpen={(id) => navigate(`/customers/${id}`)} />
          {customers.hasNextPage ? (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                isPending={customers.isFetchingNextPage}
                onPress={() => customers.fetchNextPage()}
              >
                {t("common.loadMore")}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Page>
  );
}

function CustomerTable({ rows, onOpen }: { rows: CustomerSummary[]; onOpen: (id: string) => void }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content
          aria-label={t("customers.title")}
          className="min-w-[760px]"
          onRowAction={(k) => onOpen(String(k))}
        >
          <Table.Header>
            <Table.Column isRowHeader>{t("customers.columns.customer")}</Table.Column>
            <Table.Column>{t("customers.columns.channels")}</Table.Column>
            <Table.Column>{t("customers.columns.app")}</Table.Column>
            <Table.Column>{t("customers.columns.location")}</Table.Column>
            <Table.Column>{t("customers.columns.lastActive")}</Table.Column>
          </Table.Header>
          <Table.Body
            renderEmptyState={() => (
              <div className="py-10 text-center text-sm text-muted">{t("customers.noResults")}</div>
            )}
          >
            {rows.map((c) => (
              <Table.Row key={c.id} id={c.id} className="cursor-pointer">
                <Table.Cell>
                  <span className="flex max-w-[240px] flex-col">
                    <span className="truncate text-sm font-medium">{c.name ?? c.external_id}</span>
                    {c.name ? (
                      <span className="truncate font-mono text-xs text-muted">{c.external_id}</span>
                    ) : null}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <ReachChips channels={c.channels} />
                </Table.Cell>
                <Table.Cell className="text-sm">{c.app.name}</Table.Cell>
                <Table.Cell className="text-sm">
                  {[c.city, c.country].filter(Boolean).join(", ") || <span className="text-muted">—</span>}
                </Table.Cell>
                <Table.Cell className="text-sm whitespace-nowrap">
                  <span title={fmt.dateTime(c.last_active_at)}>{fmt.relative(c.last_active_at)}</span>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  );
}

/** One chip per channel; channels with no usable address are shown as a warning. */
export function ReachChips({ channels }: { channels: CustomerSummary["channels"] }) {
  const { t } = useTranslation();
  if (channels.length === 0) return <span className="text-sm text-muted">{t("common.none")}</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {channels.map((c) => (
        <Chip
          key={c.channel}
          size="sm"
          variant={c.active > 0 ? "secondary" : "soft"}
          color={c.active > 0 ? "default" : "warning"}
          title={c.active < c.total ? t("customers.invalidCount", { count: c.total - c.active }) : undefined}
        >
          {t(`channels.${c.channel}`)}
          {c.total > 1 ? <span className="text-muted tabular-nums">×{c.total}</span> : null}
          {c.active === 0 ? <span aria-hidden> !</span> : null}
        </Chip>
      ))}
    </div>
  );
}
